import React, { useState, useEffect } from 'react';
import Modal from '../../../BaseComponents/Modal';
import Button from '../../../BaseComponents/Button';
import { NoteTemplate, getNoteTemplateById } from '../../../../api/note-templates';
import { NOTE_TEMPLATE_CATEGORIES } from '../../../../utils/constants';
import trash from "../../../../assets/icons/TrashBin2.svg";
import './NoteTemplateModal.scss';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    template: NoteTemplate | null;
    onEdit?: (template: NoteTemplate) => void;
    onDelete?: (template: NoteTemplate) => void;
};

const ViewNoteTemplateModal: React.FC<Props> = ({ isOpen, onClose, template, onEdit, onDelete }) => {
    const [fullTemplate, setFullTemplate] = useState<NoteTemplate | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && template?.id) {
            setIsLoading(true);
            setError(null);
            getNoteTemplateById(template.id)
                .then((data) => {
                    setFullTemplate(data);
                })
                .catch((err) => {
                    console.error('Error fetching note template details:', err);
                    setError('Failed to load template details');
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [isOpen, template?.id]);

    if (!template) return null;

    const displayTemplate = fullTemplate || template;
    const categoryLabel = NOTE_TEMPLATE_CATEGORIES.find(
        (cat) => cat.key === displayTemplate.category
    )?.value || displayTemplate.category;

    const handleEdit = () => {
        if (onEdit) {
            onEdit(displayTemplate);
            onClose();
        }
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete(displayTemplate);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Note Template"
            width="900px"
        >
            <div className="note-template-view-modal">
                {isLoading ? (
                    <div className="loading-state" style={{ padding: '60px', textAlign: 'center' }}>
                        Loading template details...
                    </div>
                ) : error ? (
                    <div className="error-state" style={{ padding: '60px', textAlign: 'center', color: 'red' }}>
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="template-details">
                            <div className="detail-field">
                                <label className="field-label">Name of Template</label>
                                <div className="field-value">{displayTemplate.name}</div>
                            </div>

                            <div className="detail-field">
                                <label className="field-label">Category</label>
                                <div className="field-value">{categoryLabel}</div>
                            </div>

                            <div className="detail-field">
                                <label className="field-label">Template Content</label>
                                <div className="field-value content-box">
                                    <div dangerouslySetInnerHTML={{ __html: displayTemplate.template }} />
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <Button
                                text="Edit"
                                onClick={handleEdit}
                                isSecondary={false}
                            />
                            <button
                                className="delete-button"
                                onClick={handleDelete}
                            >
                                <img src={trash} alt="delete" />
                                Delete note template
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ViewNoteTemplateModal;
